import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

// ── Paleta Zafiro ceniza matutino ─────────────────────────────────────────
const ACCENT    = "#35627A";
const RED       = "#B46258";
const VIOLET    = "#A6A9D0";
const ROSE      = "#E5AEA9";
const GRAY      = "#8E9A98";
const BORDER    = "#e2e5e4";
const BG        = "#F5F5F5";

const PESTEL_COLORS = {
  Político:    "#35627A",
  Económico:   "#059669",
  Social:      "#B46258",
  Tecnológico: "#A6A9D0",
  Ambiental:   "#8E9A98",
  Legal:       "#E5AEA9",
};

const HORIZON_ORDER  = ["Corto plazo", "Medio plazo", "Largo plazo"];
const HORIZON_WIDTHS = { "Corto plazo": 1.0, "Mediano plazo": 0.6, "Largo plazo": 0.3 };

// ── TTM index ────────────────────────────────────────────────────────────
function calcTTM(signals) {
  if (!signals.length) return 0;
  // TTM diferenciado: pondera por horizonte, impacto Y ratio oportunidad/riesgo
  const opps = signals.filter(s => s.classification === "Oportunidad").length;
  const ratio_opp = opps / signals.length;
  const sum = signals.reduce((acc, s) => {
    const w = HORIZON_WIDTHS[s.time_horizon] ?? 0.3;
    const impact = +s.impact_probability || 0;
    const clasificacion_bonus = s.classification === "Oportunidad" ? 1.2 : 0.7;
    return acc + (impact * w * clasificacion_bonus);
  }, 0);
  const base = (sum / signals.length) * 100;
  // Ajustar por ratio de oportunidades
  const ajustado = base * (0.5 + ratio_opp * 0.8);
  return Math.min(100, Math.round(ajustado));
}

function RoadmapChart({ signals }) {
  const ref = useRef(null);

  useEffect(() => {
    if (!signals.length || !ref.current) return;
    const el  = ref.current;
    const W   = el.clientWidth || 700;
    const ROW = 48;
    const PAD = { top: 50, right: 30, bottom: 30, left: 120 };

    const CATS = ["Político", "Económico", "Social", "Tecnológico", "Ambiental", "Legal"];
    const YEARS = [2025, 2027, 2030, 2033, 2037, 2040];

    // Mapear time_horizon a año central
    const horizonToYear = {
      "Corto plazo":   2026,
      "Mediano plazo": 2030,
      "Largo plazo":   2037,
    };

    const H = PAD.top + CATS.length * (ROW + 8) + PAD.bottom;
    const iW = W - PAD.left - PAD.right;
    const iH = H - PAD.top - PAD.bottom;

    d3.select(el).selectAll("*").remove();
    const svg = d3.select(el)
      .append("svg").attr("width", W).attr("height", H);
    const g = svg.append("g")
      .attr("transform", `translate(${PAD.left},${PAD.top})`);

    // Escala X temporal
    const xScale = d3.scaleLinear()
      .domain([2024, 2041])
      .range([0, iW]);

    // Fondo de horizontes
    const horizonBands = [
      { label: "CORTO PLAZO",   x1: 2024.5, x2: 2028.5, color: "rgba(53,98,122,0.04)"  },
      { label: "MEDIANO PLAZO", x1: 2028.5, x2: 2033.5, color: "rgba(166,169,208,0.06)" },
      { label: "LARGO PLAZO",   x1: 2033.5, x2: 2040.5, color: "rgba(5,150,105,0.04)"  },
    ];

    horizonBands.forEach(band => {
      g.append("rect")
        .attr("x", xScale(band.x1))
        .attr("y", -PAD.top + 4)
        .attr("width", xScale(band.x2) - xScale(band.x1))
        .attr("height", H - 8)
        .attr("fill", band.color);

      g.append("text")
        .attr("x", xScale((band.x1 + band.x2) / 2))
        .attr("y", -28)
        .attr("text-anchor", "middle")
        .attr("fill", ACCENT)
        .attr("font-size", 9)
        .attr("font-weight", "700")
        .attr("font-family", "JetBrains Mono, monospace")
        .attr("letter-spacing", 1)
        .text(band.label);
    });

    // Líneas de año
    YEARS.forEach(year => {
      g.append("line")
        .attr("x1", xScale(year)).attr("x2", xScale(year))
        .attr("y1", -PAD.top + 4).attr("y2", iH + 10)
        .attr("stroke", BORDER)
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "4,4");

      g.append("text")
        .attr("x", xScale(year))
        .attr("y", -10)
        .attr("text-anchor", "middle")
        .attr("fill", GRAY)
        .attr("font-size", 9)
        .attr("font-family", "JetBrains Mono, monospace")
        .text(year);
    });

    // Filas por categoría PESTEL
    CATS.forEach((cat, ci) => {
      const y = ci * (ROW + 8);
      const color = PESTEL_COLORS[cat] || ACCENT;

      // Fondo de fila alternado
      if (ci % 2 === 0) {
        g.append("rect")
          .attr("x", 0).attr("y", y - 4)
          .attr("width", iW).attr("height", ROW + 8)
          .attr("fill", "rgba(0,0,0,0.01)");
      }

      // Label categoría
      g.append("text")
        .attr("x", -10).attr("y", y + ROW / 2 + 4)
        .attr("text-anchor", "end")
        .attr("fill", color)
        .attr("font-size", 10)
        .attr("font-weight", "600")
        .attr("font-family", "Inter, sans-serif")
        .text(cat);

      // Línea horizontal de categoría
      g.append("line")
        .attr("x1", 0).attr("x2", iW)
        .attr("y1", y + ROW + 4).attr("y2", y + ROW + 4)
        .attr("stroke", BORDER)
        .attr("stroke-width", 0.5);

      // Señales de esta categoría
      const catSignals = signals.filter(s => s.pestel_category === cat);
      
      // Agrupar por horizonte y posicionar con jitter
      const byHorizon = {};
      catSignals.forEach(s => {
        const h = s.time_horizon || "Largo plazo";
        if (!byHorizon[h]) byHorizon[h] = [];
        byHorizon[h].push(s);
      });

      Object.entries(byHorizon).forEach(([horizon, hSigs]) => {
        const baseYear = horizonToYear[horizon] || 2037;
        const isRisk   = hSigs.filter(s => s.classification === "Riesgo").length > hSigs.length / 2;
        const avgImp   = hSigs.reduce((a, s) => a + (+s.impact_probability || 0), 0) / hSigs.length;
        const trl      = horizon === "Corto plazo" ? "TRL 6-9" : horizon === "Mediano plazo" ? "TRL 4-6" : "TRL 1-3";
        
        // Tamaño del elemento según impacto
        const rW = Math.max(90, Math.min(130, avgImp * 150));
        const rH = 36;
        const cx = xScale(baseYear);
        const cy = y + (ROW - rH) / 2;

        // Sombra
        g.append("rect")
          .attr("x", cx - rW / 2 + 2).attr("y", cy + 2)
          .attr("width", rW).attr("height", rH)
          .attr("rx", 6)
          .attr("fill", "rgba(0,0,0,0.06)");

        // Rectángulo principal
        g.append("rect")
          .attr("x", cx - rW / 2).attr("y", cy)
          .attr("width", rW).attr("height", rH)
          .attr("rx", 6)
          .attr("fill", isRisk ? `${RED}20` : `${color}20`)
          .attr("stroke", isRisk ? RED : color)
          .attr("stroke-width", 1.5);

        // Barra lateral izquierda (indicador TRL)
        g.append("rect")
          .attr("x", cx - rW / 2).attr("y", cy)
          .attr("width", 4).attr("height", rH)
          .attr("rx", 3)
          .attr("fill", isRisk ? RED : color);

        // Texto principal — número de señales
        g.append("text")
          .attr("x", cx - rW / 2 + 10).attr("y", cy + 13)
          .attr("fill", isRisk ? RED : color)
          .attr("font-size", 11)
          .attr("font-weight", "700")
          .attr("font-family", "JetBrains Mono, monospace")
          .text(`${hSigs.length} señ.`);

        // Impacto promedio
        g.append("text")
          .attr("x", cx - rW / 2 + 10).attr("y", cy + 26)
          .attr("fill", GRAY)
          .attr("font-size", 8)
          .attr("font-family", "JetBrains Mono, monospace")
          .text(`${Math.round(avgImp * 100)}% · ${trl}`);

        // Tooltip
        g.append("title").text(
          `${cat} · ${horizon}\n${hSigs.length} señales\nImpacto promedio: ${Math.round(avgImp * 100)}%\n${trl}`
        );
      });
    });

  }, [signals]);

  return <div ref={ref} style={{ width: "100%" }} />;
}

// ── KPI Card ─────────────────────────────────────────────────────────────
function KpiCard({ item }) {
  const up   = item.variacion >= 0;
  const pct  = Math.abs(item.variacion).toFixed(2);
  const val  = item.valor >= 1000
    ? item.valor.toLocaleString("es-CO", { maximumFractionDigits: 0 })
    : item.valor.toLocaleString("es-CO", { maximumFractionDigits: 2 });

  const typeColors = {
    commodity: { bg: "rgba(53,98,122,0.07)",  border: "rgba(53,98,122,0.2)",  text: ACCENT },
    divisa:    { bg: "rgba(166,169,208,0.12)", border: "rgba(166,169,208,0.3)", text: "#5557a0" },
    indice:    { bg: "rgba(5,150,105,0.07)",   border: "rgba(5,150,105,0.2)",  text: "#065f46" },
  };
  const tc = typeColors[item.tipo] || typeColors.commodity;

  return (
    <div style={{
      background: "#fff",
      border: `1px solid ${BORDER}`,
      borderRadius: 10,
      padding: "14px 16px",
      minWidth: 130,
      flex: "1 1 130px",
      boxShadow: "0 1px 3px rgba(53,98,122,0.06)",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Type badge */}
      <div style={{
        display: "inline-block",
        padding: "2px 7px",
        borderRadius: 20,
        fontSize: 9,
        fontFamily: "JetBrains Mono, monospace",
        fontWeight: 600,
        background: tc.bg,
        border: `1px solid ${tc.border}`,
        color: tc.text,
        marginBottom: 8,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}>{item.tipo}</div>

      {/* Indicator name */}
      <div style={{ fontSize: 12, fontWeight: 600, color: "#1a1a1a", marginBottom: 6, lineHeight: 1.2 }}>
        {item.indicador}
      </div>

      {/* Value */}
      <div style={{ fontSize: 20, fontWeight: 700, color: "#111111", lineHeight: 1, marginBottom: 6 }}>
        {val}
      </div>

      {/* Variation */}
      <div style={{
        display: "flex", alignItems: "center", gap: 4,
        fontSize: 11, fontWeight: 600,
        color: up ? "#065f46" : "#8a3f35",
      }}>
        <span style={{ fontSize: 13 }}>{up ? "↑" : "↓"}</span>
        <span>{pct}%</span>
      </div>

      {/* Country tag */}
      <div style={{
        position: "absolute", top: 12, right: 12,
        fontSize: 9, color: GRAY,
        fontFamily: "JetBrains Mono, monospace",
      }}>{item.pais}</div>

      {/* Accent bar bottom */}
      <div style={{
        position: "absolute", bottom: 0, left: 0, right: 0,
        height: 3,
        background: up
          ? "rgba(5,150,105,0.3)"
          : "rgba(180,98,88,0.3)",
        borderRadius: "0 0 10px 10px",
      }} />
    </div>
  );
}

// ── TTM Card ─────────────────────────────────────────────────────────────
function TTMCard({ ttm, signals }) {
  const riesgos = signals.filter(s => s.classification === "Riesgo").length;
  const opps    = signals.filter(s => s.classification === "Oportunidad").length;
  const level   = ttm >= 70 ? "Alto" : ttm >= 40 ? "Medio" : "Bajo";
  const levelColor = ttm >= 70 ? RED : ttm >= 40 ? "#B46258" : ACCENT;

  return (
    <div style={{
      background: "#fff",
      border: `1px solid ${BORDER}`,
      borderRadius: 10,
      padding: "18px 20px",
      boxShadow: "0 1px 3px rgba(53,98,122,0.06)",
      display: "flex", gap: 24, alignItems: "center",
      flexWrap: "wrap",
    }}>
      {/* TTM gauge */}
      <div style={{ textAlign: "center", minWidth: 100 }}>
        <div style={{ fontSize: 9, fontFamily: "JetBrains Mono, monospace", color: GRAY, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>
          Time-to-Market
        </div>
        <div style={{
          fontSize: 42, fontWeight: 700, lineHeight: 1,
          color: levelColor,
        }}>{ttm}</div>
        <div style={{ fontSize: 10, color: GRAY, marginTop: 4 }}>/ 100</div>
        <div style={{
          marginTop: 8, display: "inline-block",
          padding: "3px 10px", borderRadius: 20,
          fontSize: 10, fontWeight: 600,
          background: `${levelColor}18`,
          border: `1px solid ${levelColor}40`,
          color: levelColor,
        }}>{level}</div>
      </div>

      {/* Divider */}
      <div style={{ width: 1, height: 80, background: BORDER }} />

      {/* Stats */}
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#111" }}>{signals.length}</div>
          <div style={{ fontSize: 11, color: GRAY, marginTop: 2 }}>señales</div>
        </div>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: RED }}>{riesgos}</div>
          <div style={{ fontSize: 11, color: GRAY, marginTop: 2 }}>riesgos</div>
        </div>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: ACCENT }}>{opps}</div>
          <div style={{ fontSize: 11, color: GRAY, marginTop: 2 }}>oportunidades</div>
        </div>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: "#111" }}>
            {signals.length ? Math.round(signals.reduce((a, s) => a + (+s.impact_probability || 0), 0) / signals.length * 100) : 0}%
          </div>
          <div style={{ fontSize: 11, color: GRAY, marginTop: 2 }}>impacto prom.</div>
        </div>
      </div>
    </div>
  );
}

// ── Main Forecast component ──────────────────────────────────────────────
// ── VAB Chart ─────────────────────────────────────────────────────────────
function VABChart() {
  const ref = useRef(null);
  const data = [
    { year: "2019", vab: 1091 },
    { year: "2020", vab: 998  },
    { year: "2021", vab: 1539 },
    { year: "2022", vab: 2002 },
    { year: "2023", vab: 2153 },
    { year: "2024", vab: 2148 },
  ];

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const W  = el.clientWidth || 600;
    const H  = 160;
    const PAD = { top: 20, right: 20, bottom: 30, left: 60 };

    d3.select(el).selectAll("*").remove();
    const svg = d3.select(el).append("svg").attr("width", W).attr("height", H);
    const g   = svg.append("g").attr("transform", `translate(${PAD.left},${PAD.top})`);
    const iW  = W - PAD.left - PAD.right;
    const iH  = H - PAD.top - PAD.bottom;

    const x = d3.scaleBand().domain(data.map(d => d.year)).range([0, iW]).padding(0.3);
    const y = d3.scaleLinear().domain([0, 2400]).range([iH, 0]);

    // Grid lines
    y.ticks(4).forEach(t => {
      g.append("line")
        .attr("x1", 0).attr("x2", iW)
        .attr("y1", y(t)).attr("y2", y(t))
        .attr("stroke", BORDER).attr("stroke-dasharray", "3,3");
      g.append("text")
        .attr("x", -6).attr("y", y(t) + 4)
        .attr("text-anchor", "end")
        .attr("fill", GRAY).attr("font-size", 9)
        .text(t.toLocaleString("es-CO"));
    });

    // Bars
    g.selectAll("rect")
      .data(data)
      .join("rect")
      .attr("x", d => x(d.year))
      .attr("y", d => y(d.vab))
      .attr("width", x.bandwidth())
      .attr("height", d => iH - y(d.vab))
      .attr("fill", ACCENT)
      .attr("fill-opacity", 0.7)
      .attr("rx", 4);

    // Value labels
    g.selectAll(".label")
      .data(data)
      .join("text")
      .attr("class", "label")
      .attr("x", d => x(d.year) + x.bandwidth() / 2)
      .attr("y", d => y(d.vab) - 4)
      .attr("text-anchor", "middle")
      .attr("fill", ACCENT)
      .attr("font-size", 9)
      .attr("font-weight", "600")
      .text(d => d.vab.toLocaleString("es-CO"));

    // X axis
    g.selectAll(".xtick")
      .data(data)
      .join("text")
      .attr("class", "xtick")
      .attr("x", d => x(d.year) + x.bandwidth() / 2)
      .attr("y", iH + 16)
      .attr("text-anchor", "middle")
      .attr("fill", GRAY)
      .attr("font-size", 10)
      .text(d => d.year);

  }, []);

  return <div ref={ref} style={{ width: "100%" }} />;
}

export default function Forecast({ signals = [], financial = [], forecastText = null }) {
  const ttm = calcTTM(signals);

  const byTipo = { commodity: [], divisa: [], indice: [] };
  financial.forEach(f => {
    const t = f.tipo?.[0] ?? f.tipo ?? "commodity";
    if (byTipo[t]) byTipo[t].push({
      indicador: f.indicador?.[0] ?? f.indicador,
      valor:     f.valor?.[0]     ?? f.valor,
      variacion: f.variacion?.[0] ?? f.variacion,
      tipo:      t,
      pais:      f.pais?.[0]      ?? f.pais,
    });
  });

  const cardStyle = {
    background: "#fff",
    border: `1px solid ${BORDER}`,
    borderRadius: 10,
    padding: "18px 20px",
    marginBottom: 14,
    boxShadow: "0 1px 3px rgba(53,98,122,0.06)",
  };
  const titleStyle = {
    fontSize: 11, color: GRAY,
    textTransform: "uppercase", letterSpacing: ".8px",
    marginBottom: 14, display: "flex", alignItems: "center", gap: 6,
    fontWeight: 600,
  };
  const sectionLabel = (icon, text) => (
    <div style={titleStyle}><span>{icon}</span>{text}</div>
  );

  return (
    <div>
      {/* TTM + resumen */}
      <div style={cardStyle}>
        {sectionLabel("⏱", "Índice Time-to-Market")}
        <TTMCard ttm={ttm} signals={signals} />
      </div>

      {/* Gantt temporal */}
      <div style={cardStyle}>
        {sectionLabel("📅", "Horizonte de impacto por categoría")}
        {signals.length > 0
          ? <RoadmapChart signals={signals} />
          : <div style={{ color: GRAY, fontSize: 13, textAlign: "center", padding: 24 }}>Sin señales disponibles.</div>
        }
        
        {/* Leyenda compacta */}
        <div style={{
          display: "flex", alignItems: "center", gap: 16,
          marginTop: 12, flexWrap: "wrap",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: GRAY }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: `${ACCENT}30`, border: `1.5px solid ${ACCENT}` }} />
            Oportunidad
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, color: GRAY }}>
            <div style={{ width: 12, height: 12, borderRadius: 3, background: `${RED}30`, border: `1.5px solid ${RED}` }} />
            Riesgo
          </div>
          <div style={{ fontSize: 10, color: GRAY, fontFamily: "JetBrains Mono, monospace" }}>
            · Tamaño proporcional al impacto promedio · % = probabilidad de impacto
          </div>
        </div>
      </div>

      {/* Mercado potencial bioeconomía — datos DANE */}
      <div style={cardStyle}>
        {sectionLabel("💰", "Mercado potencial — Bioeconomía Colombia (DANE 2024)")}
        
        {/* KPIs principales */}
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
          {[
            { label: "VAB Bioeconomía 2024", value: "COP 2.148B", sub: "Miles de millones", color: ACCENT },
            { label: "Participación PIB", value: "0.13%", sub: "Con potencial al 10%", color: "#059669" },
            { label: "Crecimiento 2019-2024", value: "+97%", sub: "A precios corrientes", color: "#059669" },
            { label: "Sector secundario", value: "76%", sub: "Del VAB bioeconomía", color: ACCENT },
          ].map((kpi, i) => (
            <div key={i} style={{
              flex: "1 1 140px", background: BG,
              border: `1px solid ${BORDER}`, borderRadius: 10,
              padding: "14px 16px",
            }}>
              <div style={{ fontSize: 10, color: GRAY, fontFamily: "JetBrains Mono, monospace", textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 8 }}>
                {kpi.label}
              </div>
              <div style={{ fontSize: 22, fontWeight: 700, color: kpi.color, marginBottom: 4 }}>
                {kpi.value}
              </div>
              <div style={{ fontSize: 10, color: GRAY }}>{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* Gráfico de evolución VAB */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, color: GRAY, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
            Evolución VAB Bioeconomía Colombia (Miles de millones COP)
          </div>
          <VABChart />
        </div>

        {/* Estimación potencial sectorial */}
        <div style={{
          background: "rgba(53,98,122,0.04)",
          border: `1px solid rgba(53,98,122,0.15)`,
          borderRadius: 8, padding: "14px 16px",
        }}>
          <div style={{ fontSize: 10, color: ACCENT, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
            Potencial de mercado por sector
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { sector: "Agroindustria", potencial: "USD ~180M", horizonte: "H1-H2", color: "#059669" },
              { sector: "Cosméticos naturales", potencial: "USD ~45M", horizonte: "H1", color: ACCENT },
              { sector: "Bioinsumos agrícolas", potencial: "USD ~75M", horizonte: "H1-H2", color: "#059669" },
              { sector: "Farmacéutica verde", potencial: "USD ~35M", horizonte: "H2-H3", color: VIOLET },
            ].map((s, i) => (
              <div key={i} style={{
                flex: "1 1 140px", background: "#fff",
                border: `1px solid ${BORDER}`, borderRadius: 8,
                padding: "10px 12px",
              }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#111", marginBottom: 4 }}>{s.sector}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: s.color, marginBottom: 2 }}>{s.potencial}</div>
                <div style={{ fontSize: 9, color: GRAY, fontFamily: "JetBrains Mono, monospace" }}>{s.horizonte}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 10, color: GRAY, marginTop: 10 }}>
            * Estimaciones basadas en participación sectorial DANE y benchmarks regionales. Tipo de cambio ~COP 4.050/USD.
          </div>
        </div>
      </div>
    </div>
  );
}
